import AbstractPromotionsService from './abstract-promotions-service';
import MojitoCore from 'mojito/core';
import ApiRequestProvider from 'services/common/api-request/request-provider.js';
import TaskRunner from 'services/common/task-runner/task-runner';
import PollUserPromotionsTask from 'services/promotions/task/poll-user-promotions-task';
import { without } from 'mojito/utils';

const DEFAULT_INTERVAL = 60 * 2 * 1000;

const logger = MojitoCore.logger.get('PollPromotionsService');

const BASE_URL = '/promotions';

/**
 * Promotions service config.
 *
 * @typedef PollPromotionsServiceConfig
 * @type {object}
 * @property {string} serviceUrl - Promotions API endpoint URL.
 *
 * @memberof Mojito.Services.Promotions
 */

/**
 * Class offering the possibility to interact with the mojito Promotions API using polling mechanism.
 * This implementation is typically used with Quantum integration.
 *
 * @class PollPromotionsService
 * @name pollPromotionsService
 * @extends Mojito.Services.Promotions.AbstractPromotionsService
 * @memberof Mojito.Services.Promotions
 */
export default class PollPromotionsService extends AbstractPromotionsService {
    constructor() {
        super();
        this.subscribers = [];
        this.promotionsPoller = new TaskRunner(DEFAULT_INTERVAL);
    }

    /**
     * Configure the service.
     *
     * @param {Mojito.Services.Promotions.PollPromotionsServiceConfig} config - Config object.
     *
     * @function Mojito.Services.Promotions.PollPromotionsService#configure
     */
    configure(config = {}) {
        this.serviceUrl = config.serviceUrl;
        this.requestFactory = new ApiRequestProvider(logger);
        this.apiUrl = `${this.serviceUrl + BASE_URL}`;
    }

    /**
     * Configure polling interval.
     *
     * @param {number} intervalSec - Polling interval in seconds. Default is 120 seconds.
     *
     * @returns {Mojito.Services.Promotions.PollPromotionsService} The PollPromotionsService instance.
     * @function Mojito.Services.Promotions.PollPromotionsService#withInterval
     */
    withInterval(intervalSec) {
        this.pollInterval = (intervalSec && intervalSec * 1000) || DEFAULT_INTERVAL;
        this.promotionsPoller = new TaskRunner(this.pollInterval);
        return this;
    }

    subscribe(options, onMessage, onFailed) {
        const { clientId, channels } = options;
        this.subscribers.push(clientId);
        if (this.promotionsPoller.isExecuting || this.promotionsPoller.activeTask) {
            return;
        }
        // In case of empty response, we fallback to the last channel id which is expected to be the one that was most recently returned by server when data existed.
        // Quantum doesn't support user groups which means that channels list will, typically, contain only one channel id.
        const fallbackId = channels[channels.length - 1];
        const task = new PollUserPromotionsTask(
            this,
            data => onMessage(data?.id || fallbackId, data),
            onFailed
        );
        this.promotionsPoller.run(task);
    }

    unsubscribe(options) {
        const { clientId } = options;
        this.subscribers = without(this.subscribers, clientId);
        if (!this.subscribers.length) {
            this.promotionsPoller?.reset();
        }
    }

    reset() {
        this.subscribers = [];
        this.promotionsPoller?.reset();
    }

    getPromotions() {
        return this.requestFactory
            .get(`${this.apiUrl}`)
            .withDescription('get promotions')
            .withCredentials()
            .send();
    }
}

/**
 * PollPromotionsService class instance.
 *
 * @name pollPromotionsService
 * @memberof Mojito.Services.Promotions
 */
export const pollPromotionsService = new PollPromotionsService();
